import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Send, ArrowLeft, Paperclip, Mic, Square, FileText, Loader2, Play, Pause, Plus, MoreVertical, Pin, PinOff, Flag, Trash2, Ban, Users, Link2, LogOut, Check, X, UserCheck, UserX } from "lucide-react";
import { api, uploadImage } from "../api";
import { subscribe, unsubscribe, onEvent, sendTyping } from "../socket";
import { timeAgo, mediaUrl, fmtSize, fmtDur } from "../config";
import { Avatar, Spinner, Empty } from "../components/cards";
import { NewChatModal, GroupSettingsModal } from "../components/chat";
import { useAuth } from "../auth";

const convName = (c, myId) => {
  if (c.type === "group") return c.name || c.title || "Group";
  if (c.title || c.name) return c.title || c.name;
  const other = c.other || (c.members || []).find((p) => p.id !== myId);
  return other?.displayName || other?.username || "Conversation";
};

const convAvatarUser = (c, myId) => {
  if (c.type === "group") return { avatar: c.avatar, displayName: c.name || c.title };
  return c.other || (c.members || []).find((p) => p.id !== myId) || c;
};

const isRequest = (c) => !!(c.isRequest || c.request || c.status === "request" || c.status === "pending");

const REACTION_EMOJIS = ["❤️", "👍", "😂", "😮", "🙏"];

const MessageMenu = ({ m, mine, onPick, onPin, onReport, onDeleteForMe, onDeleteForEveryone, pinned }) => (
  <div
    className={`absolute -top-12 right-0 z-20 w-52 rounded-2xl border border-white/10 bg-surface p-2 shadow-2xl ${mine ? "" : "left-0"}`}
    style={mine ? {} : { right: "auto" }}
    data-testid="message-menu"
    onClick={(e) => e.stopPropagation()}
  >
    <div className="flex justify-between px-1 pb-1">
      {REACTION_EMOJIS.map((emoji, i) => (
        <button key={emoji} onClick={() => onPick(emoji)} className="rounded-full p-1 text-lg transition-transform duration-150 hover:scale-125" data-testid={`react-option-${i}`}>
          {emoji}
        </button>
      ))}
    </div>
    <div className="border-t border-white/10 pt-1">
      <button onClick={onPin} className="flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-left text-xs font-semibold text-slate-200 hover:bg-white/5" data-testid="menu-pin-message">
        {pinned ? <PinOff size={13} /> : <Pin size={13} />} {pinned ? "Unpin message" : "Pin message"}
      </button>
      <button onClick={onReport} className="flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-left text-xs font-semibold text-slate-200 hover:bg-white/5" data-testid="menu-report-message">
        <Flag size={13} /> Report message
      </button>
      <button onClick={onDeleteForMe} className="flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-left text-xs font-semibold text-slate-200 hover:bg-white/5" data-testid="menu-delete-for-me">
        <Trash2 size={13} /> Delete for me
      </button>
      {mine && (
        <button onClick={onDeleteForEveryone} className="flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-left text-xs font-semibold text-crimson hover:bg-crimson/10" data-testid="menu-delete-for-everyone">
          <Trash2 size={13} /> Delete for everyone
        </button>
      )}
    </div>
  </div>
);

const ReactionPills = ({ m, mine, myId, onToggle }) => {
  const entries = Object.entries(m.reactions || {}).filter(([, v]) => (Array.isArray(v) ? v.length : Number(v) || 0) > 0);
  if (!entries.length) return null;
  return (
    <div className={`mt-1 flex flex-wrap gap-1 ${mine ? "justify-end" : "justify-start"}`} data-testid={`reactions-${m.id}`}>
      {entries.map(([emoji, val], idx) => {
        const count = Array.isArray(val) ? val.length : val;
        const iReacted = Array.isArray(val) && val.includes(myId);
        return (
          <button
            key={emoji}
            onClick={(e) => {
              e.stopPropagation();
              onToggle(emoji);
            }}
            data-testid={`reaction-pill-${m.id}-${idx}`}
            className={`flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs transition-colors ${
              iReacted ? "border-crimson/50 bg-crimson/15" : "border-white/10 bg-white/[0.05] hover:border-white/25"
            }`}
          >
            <span>{emoji}</span>
            <span className="font-mono text-[10px] text-slate-400">{count}</span>
          </button>
        );
      })}
    </div>
  );
};

const VoiceNote = ({ m, mine }) => {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [bars, setBars] = useState(null);
  const [dur, setDur] = useState(m.durationSec || 0);
  const src = mediaUrl(m.audioUrl);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(src);
        const buf = await res.arrayBuffer();
        const ctx = new (window.AudioContext || window.webkitAudioContext)();
        const decoded = await ctx.decodeAudioData(buf);
        const data = decoded.getChannelData(0);
        const n = 42;
        const block = Math.floor(data.length / n) || 1;
        const peaks = [];
        for (let i = 0; i < n; i++) {
          let max = 0;
          for (let j = 0; j < block; j += 64) max = Math.max(max, Math.abs(data[i * block + j] || 0));
          peaks.push(max);
        }
        const top = Math.max(...peaks, 0.01);
        if (!cancelled) setBars(peaks.map((p) => Math.max(0.15, p / top)));
        ctx.close();
      } catch {
        if (!cancelled) {
          let seed = [...String(m.id)].reduce((a, c) => a + c.charCodeAt(0), 7);
          const rand = () => {
            seed = (seed * 9301 + 49297) % 233280;
            return seed / 233280;
          };
          setBars(Array.from({ length: 42 }, () => 0.2 + rand() * 0.8));
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [src, m.id]);

  const toggle = () => {
    const el = audioRef.current;
    if (!el) return;
    if (playing) el.pause();
    else el.play().catch(() => {});
  };

  const seek = (e) => {
    const el = audioRef.current;
    if (!el || !el.duration) return;
    const r = e.currentTarget.getBoundingClientRect();
    el.currentTime = ((e.clientX - r.left) / r.width) * el.duration;
  };

  const playedBars = Math.floor(progress * (bars?.length || 0));

  return (
    <div className="flex min-w-56 items-center gap-2.5" data-testid={`msg-voice-${m.id}`}>
      <audio
        ref={audioRef}
        src={src}
        preload="metadata"
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onEnded={() => {
          setPlaying(false);
          setProgress(0);
        }}
        onLoadedMetadata={(e) => setDur(e.target.duration || m.durationSec || 0)}
        onTimeUpdate={(e) => setProgress(e.target.duration ? e.target.currentTime / e.target.duration : 0)}
      />
      <button
        onClick={(e) => {
          e.stopPropagation();
          toggle();
        }}
        className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full transition-colors ${
          mine ? "bg-white/20 text-white hover:bg-white/30" : "bg-crimson text-white hover:bg-crimson-hover"
        }`}
        data-testid={`voice-play-${m.id}`}
      >
        {playing ? <Pause size={14} /> : <Play size={14} className="ml-0.5" fill="currentColor" />}
      </button>
      <div className="flex h-8 flex-1 cursor-pointer items-center gap-[2px]" onClick={(e) => { e.stopPropagation(); seek(e); }} data-testid={`voice-wave-${m.id}`}>
        {(bars || Array(42).fill(0.5)).map((h, i) => (
          <span
            key={i}
            className={`w-[3px] rounded-full transition-colors ${
              i < playedBars ? (mine ? "bg-white" : "bg-crimson") : mine ? "bg-white/35" : "bg-slate-600"
            }`}
            style={{ height: `${Math.round(h * 100)}%` }}
          />
        ))}
      </div>
      <span className={`shrink-0 font-mono text-[10px] ${mine ? "text-white/70" : "text-slate-500"}`}>
        {fmtDur(playing || progress > 0 ? (audioRef.current?.currentTime ?? 0) : dur)}
      </span>
    </div>
  );
};

const MessageBody = ({ m, mine }) => {
  const media = mediaUrl(m.mediaUrl);
  const audio = mediaUrl(m.audioUrl);

  if (m.type === "image" && media) {
    return (
      <div>
        <a href={media} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}>
          <img src={media} alt={m.fileName || "image"} loading="lazy" className="max-w-[260px] rounded-xl" data-testid={`msg-image-${m.id}`} />
        </a>
        {m.text && <p className="mt-2">{m.text}</p>}
      </div>
    );
  }
  if (m.type === "video" && media) {
    return (
      <div onClick={(e) => e.stopPropagation()}>
        <video src={media} controls playsInline className="max-w-[280px] rounded-xl" data-testid={`msg-video-${m.id}`} />
        {m.text && <p className="mt-2">{m.text}</p>}
      </div>
    );
  }
  if (m.type === "audio" && audio) {
    return (
      <div className="min-w-56" data-testid={`msg-audio-${m.id}`} onClick={(e) => e.stopPropagation()}>
        {m.fileName && <p className="mb-1.5 truncate text-xs opacity-80">{m.fileName}</p>}
        <audio src={audio} controls className="w-full max-w-64" />
      </div>
    );
  }
  if (m.type === "voice" && audio) {
    return <VoiceNote m={m} mine={mine} />;
  }
  if ((m.type === "file" || m.type === "document") && media) {
    return (
      <a
        href={media}
        target="_blank"
        rel="noopener noreferrer"
        className="flex min-w-48 items-center gap-3"
        data-testid={`msg-file-${m.id}`}
        onClick={(e) => e.stopPropagation()}
      >
        <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg ${mine ? "bg-white/15" : "bg-crimson/15 text-crimson"}`}>
          <FileText size={18} />
        </span>
        <span className="min-w-0">
          <span className="block truncate font-semibold">{m.fileName || "Document"}</span>
          {m.fileSize > 0 && <span className="block text-xs opacity-70">{fmtSize(m.fileSize)}</span>}
        </span>
      </a>
    );
  }
  if (m.type === "sticker") {
    const src = media || mediaUrl(m.stickerUrl);
    return src ? (
      <img src={src} alt="sticker" loading="lazy" className="h-24 w-24 object-contain" data-testid={`msg-sticker-${m.id}`} />
    ) : (
      <span className="text-4xl" data-testid={`msg-sticker-${m.id}`}>{m.text || m.sticker || "🧩"}</span>
    );
  }
  return <p>{m.text || m.content || ""}</p>;
};

const isDeleted = (m) => !m.text && !m.content && !m.mediaUrl && !m.audioUrl && m.type !== "sticker";

const Messages = () => {
  const { cid } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [convs, setConvs] = useState(null);
  const [messages, setMessages] = useState([]);
  const [active, setActive] = useState(null);
  const [text, setText] = useState("");
  const [typing, setTyping] = useState(false);
  const [busy, setBusy] = useState(false);
  const [recording, setRecording] = useState(false);
  const [recSeconds, setRecSeconds] = useState(0);
  const [recError, setRecError] = useState("");
  const [menuFor, setMenuFor] = useState(null);
  const [chatMenuOpen, setChatMenuOpen] = useState(false);
  const [newChatOpen, setNewChatOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [blockedIds, setBlockedIds] = useState([]);
  const [notice, setNotice] = useState("");
  const endRef = useRef(null);
  const typingTimer = useRef(null);
  const fileInput = useRef(null);
  const recorder = useRef(null);
  const recTimer = useRef(null);
  const recStartedAt = useRef(0);

  const loadConvs = () =>
    api.get("/conversations").then((r) => setConvs(Array.isArray(r) ? r : r.conversations || [])).catch(() => setConvs([]));

  const loadConv = () => {
    if (!cid) return;
    api.get(`/conversations/${cid}`).then(setActive).catch(() => {});
  };

  useEffect(() => {
    if (!user) return;
    loadConvs();
  }, [user]);

  useEffect(() => {
    if (!cid || !user) return;
    setMenuFor(null);
    setChatMenuOpen(false);
    loadConv();
    api.get(`/conversations/${cid}/messages`).then((r) => setMessages(Array.isArray(r) ? r : r.messages || [])).catch(() => {});
    api.post(`/conversations/${cid}/read`).catch(() => {});

    subscribe(`dm:${cid}`);
    const offMsg = onEvent("message", (ev) => {
      const m = ev.message || ev;
      if (m && (m.conversationId === cid || ev.channel === `dm:${cid}`)) {
        setMessages((ms) => (m.id && ms.some((x) => x.id === m.id) ? ms : [...ms, m]));
      }
    });
    const offUpdated = onEvent("message_updated", (ev) => {
      const m = ev.message || ev;
      if (m?.id) setMessages((ms) => ms.map((x) => (x.id === m.id ? { ...x, ...m } : x)));
    });
    const offDeleted = onEvent("message_deleted", (ev) => {
      const mid = ev.messageId || ev.message_id || ev.id;
      if (mid) setMessages((ms) => ms.map((x) => (x.id === mid ? { ...x, text: null, content: null, mediaUrl: null, audioUrl: null } : x)));
    });
    const offReaction = onEvent("reaction", (ev) => {
      const mid = ev.messageId || ev.message_id || ev.message?.id;
      if (!mid) return;
      setMessages((ms) =>
        ms.map((x) => (x.id === mid ? { ...x, ...(ev.message || {}), reactions: ev.reactions ?? ev.message?.reactions ?? x.reactions } : x))
      );
    });
    const offTyping = onEvent("typing", (ev) => {
      if (ev.conversationId === cid) {
        setTyping(!!ev.isTyping);
        if (ev.isTyping) setTimeout(() => setTyping(false), 4000);
      }
    });
    return () => {
      unsubscribe(`dm:${cid}`);
      offMsg();
      offUpdated();
      offDeleted();
      offReaction();
      offTyping();
    };
  }, [cid, user]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages.length]);

  useEffect(() => () => {
    clearInterval(recTimer.current);
    if (recorder.current && recorder.current.state !== "inactive") recorder.current.stop();
  }, []);

  if (!user) {
    return (
      <div className="mx-auto max-w-md py-20" data-testid="messages-signin-notice">
        <Empty>
          <p className="text-center">Sign in to read and send messages — your chats are the same as in the phone app.</p>
          <button onClick={() => navigate("/app/login")} className="mx-auto mt-4 block rounded-full bg-crimson px-6 py-2.5 font-heading text-xs font-bold text-white hover:bg-crimson-hover" data-testid="messages-signin-button">
            Sign in
          </button>
        </Empty>
      </div>
    );
  }

  const flash = (msg) => {
    setNotice(msg);
    setTimeout(() => setNotice(""), 2500);
  };

  const onChange = (e) => {
    setText(e.target.value);
    if (cid) {
      sendTyping(cid, true);
      clearTimeout(typingTimer.current);
      typingTimer.current = setTimeout(() => sendTyping(cid, false), 2000);
    }
  };

  const appendMessage = (m) =>
    setMessages((ms) => (m?.id && ms.some((x) => x.id === m.id) ? ms : [...ms, m]));

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim() || !cid) return;
    try {
      const m = await api.post(`/conversations/${cid}/messages`, { text: text.trim() });
      appendMessage(m);
      setText("");
      sendTyping(cid, false);
    } catch {}
  };

  const react = async (m, emoji) => {
    setMenuFor(null);
    setMessages((ms) =>
      ms.map((x) => {
        if (x.id !== m.id) return x;
        const r = { ...(x.reactions || {}) };
        const val = r[emoji];
        const mine = Array.isArray(val) && val.includes(user.id);
        if (mine) {
          r[emoji] = val.filter((u) => u !== user.id);
          if (r[emoji].length === 0) delete r[emoji];
        } else {
          r[emoji] = [...(Array.isArray(val) ? val : []), user.id];
        }
        return { ...x, reactions: r };
      })
    );
    try {
      await api.post(`/conversations/${cid}/messages/${m.id}/react`, { emoji });
    } catch {}
  };

  const pinMessage = async (m) => {
    setMenuFor(null);
    try {
      if (m.pinned) await api.del(`/conversations/${cid}/messages/${m.id}/pin`);
      else await api.post(`/conversations/${cid}/messages/${m.id}/pin`);
      setMessages((ms) => ms.map((x) => (x.id === m.id ? { ...x, pinned: !m.pinned } : x)));
    } catch {}
  };

  const reportMessage = async (m) => {
    setMenuFor(null);
    try {
      await api.post("/reports", { kind: "message", targetId: m.id, ref: m.id, conversationId: cid, reason: "Reported from the web app" });
    } catch {}
    flash("Message reported");
  };

  const deleteForMe = async (m) => {
    setMenuFor(null);
    try {
      await api.post(`/conversations/${cid}/messages/${m.id}/hide`);
      setMessages((ms) => ms.filter((x) => x.id !== m.id));
    } catch {}
  };

  const deleteForEveryone = async (m) => {
    setMenuFor(null);
    if (!window.confirm("Delete this message for everyone?")) return;
    try {
      await api.del(`/conversations/${cid}/messages/${m.id}`);
      setMessages((ms) => ms.map((x) => (x.id === m.id ? { ...x, text: null, content: null, mediaUrl: null, audioUrl: null } : x)));
    } catch {}
  };

  const sendFile = async (file) => {
    if (!file || !cid) return;
    setBusy(true);
    try {
      const kind = file.type.startsWith("image/") ? "image" : file.type.startsWith("video/") ? "video" : file.type.startsWith("audio/") ? "audio" : "file";
      const key = await uploadImage(file, "chat");
      const body = { type: kind, mediaKey: key };
      if (kind === "file") {
        body.fileName = file.name;
        body.fileSize = file.size;
      }
      const m = await api.post(`/conversations/${cid}/messages`, body);
      appendMessage(m);
    } catch {} finally {
      setBusy(false);
      if (fileInput.current) fileInput.current.value = "";
    }
  };

  const startRecording = async () => {
    setRecError("");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const rec = new MediaRecorder(stream);
      const chunks = [];
      rec.ondataavailable = (e) => chunks.push(e.data);
      rec.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const durationSec = Math.max(1, Math.round((Date.now() - recStartedAt.current) / 1000));
        const blob = new Blob(chunks, { type: rec.mimeType || "audio/webm" });
        const file = new File([blob], "voice-note.webm", { type: blob.type });
        setBusy(true);
        try {
          const key = await uploadImage(file, "chat");
          const m = await api.post(`/conversations/${cid}/messages`, { type: "voice", audioKey: key, durationSec });
          appendMessage(m);
        } catch {} finally {
          setBusy(false);
        }
      };
      recorder.current = rec;
      recStartedAt.current = Date.now();
      rec.start();
      setRecording(true);
      setRecSeconds(0);
      recTimer.current = setInterval(() => setRecSeconds(Math.round((Date.now() - recStartedAt.current) / 1000)), 500);
    } catch {
      setRecError("Microphone access is needed for voice notes.");
    }
  };

  const stopRecording = () => {
    clearInterval(recTimer.current);
    setRecording(false);
    if (recorder.current && recorder.current.state !== "inactive") recorder.current.stop();
  };

  const openChatMenu = async () => {
    setChatMenuOpen((o) => !o);
    if (!chatMenuOpen) {
      api.get("/me/blocked")
        .then((r) => setBlockedIds((Array.isArray(r) ? r : r.users || r.blocked || []).map((u) => u.id || u.userId)))
        .catch(() => {});
    }
  };

  const blockUser = async () => {
    setChatMenuOpen(false);
    const other = active?.other;
    if (!other) return;
    try {
      await api.post(`/creators/${other.id}/block`);
      setBlockedIds((ids) => (ids.includes(other.id) ? ids.filter((x) => x !== other.id) : [...ids, other.id]));
    } catch {}
  };

  const reportConversation = async () => {
    setChatMenuOpen(false);
    try {
      await api.post(`/conversations/${cid}/report`, { reason: "Reported from the web app" });
    } catch {}
    flash("Reported");
  };

  const deleteChat = async () => {
    setChatMenuOpen(false);
    if (!window.confirm("Delete this chat from your chat list?")) return;
    try {
      await api.del(`/conversations/${cid}`);
      navigate("/app/messages");
      loadConvs();
    } catch {}
  };

  const answerRequest = async (decision) => {
    try {
      await api.post(`/conversations/${cid}/${decision}`);
      if (decision === "decline") {
        navigate("/app/messages");
        loadConvs();
      } else {
        loadConv();
      }
    } catch {}
  };

  const copyGroupLink = async () => {
    setChatMenuOpen(false);
    if (!active?.inviteCode) return;
    try {
      await navigator.clipboard.writeText(`${window.location.origin}/app/join/${active.inviteCode}`);
      flash("Group link copied");
    } catch {}
  };

  if (convs === null) return <Spinner />;

  const pinnedMsg = [...messages].reverse().find((m) => m.pinned && !isDeleted(m));
  const isGroup = active?.type === "group";
  const otherBlocked = active?.other && blockedIds.includes(active.other.id);
  const canSend = active?.canSend !== false;

  const list = (
    <div className={`w-full flex-col border-white/10 lg:flex lg:w-80 lg:border-r ${cid ? "hidden lg:flex" : "flex"}`} data-testid="conversations-list">
      <div className="flex items-center justify-between border-b border-white/10 px-4 py-4">
        <p className="font-heading text-base font-bold text-white">Messages</p>
        <button onClick={() => setNewChatOpen(true)} title="New chat or group" className="rounded-full bg-crimson p-2 text-white transition-colors hover:bg-crimson-hover" data-testid="new-chat-button">
          <Plus size={16} />
        </button>
      </div>
      <div className="flex-1 overflow-y-auto">
        {convs.length === 0 && <div className="p-6"><Empty>No conversations yet. Tap + to start one.</Empty></div>}
        {convs.map((c) => (
          <button
            key={c.id}
            onClick={() => navigate(`/app/messages/${c.id}`)}
            data-testid={`conversation-${c.id}`}
            className={`flex w-full items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-white/[0.04] ${cid === c.id ? "bg-crimson/10" : ""}`}
          >
            {c.type === "group" && !mediaUrl(c.avatar) ? (
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-crimson/20 text-crimson"><Users size={16} /></span>
            ) : (
              <Avatar user={convAvatarUser(c, user?.id)} size={40} />
            )}
            <div className="min-w-0 flex-1">
              <p className="flex items-center gap-2 truncate font-heading text-sm font-semibold text-white">
                {convName(c, user?.id)}
                {isRequest(c) && <span className="shrink-0 rounded-full bg-amber-500/20 px-2 py-0.5 font-mono text-[9px] font-bold uppercase tracking-wider text-amber-400">Request</span>}
              </p>
              <p className="truncate text-xs text-slate-500">{c.lastMessage?.text || c.lastMessage?.content || (c.lastMessage?.type ? c.lastMessage.type : "")}</p>
            </div>
            {c.unreadCount > 0 && (
              <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-crimson px-1.5 font-mono text-[10px] font-bold text-white">
                {c.unreadCount}
              </span>
            )}
          </button>
        ))}
      </div>
    </div>
  );

  const chat = (
    <div className={`flex-1 flex-col ${cid ? "flex" : "hidden lg:flex"}`} data-testid="chat-pane">
      {cid ? (
        <>
          <div className="flex items-center gap-3 border-b border-white/10 px-4 py-3">
            <button onClick={() => navigate("/app/messages")} className="text-slate-400 hover:text-white lg:hidden" data-testid="chat-back-button">
              <ArrowLeft size={18} />
            </button>
            {isGroup && !mediaUrl(active?.avatar) ? (
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-crimson/20 text-crimson"><Users size={15} /></span>
            ) : (
              <Avatar user={active ? convAvatarUser(active, user?.id) : null} size={34} />
            )}
            <div className="min-w-0 flex-1">
              <p className="truncate font-heading text-sm font-bold text-white">{active ? convName(active, user?.id) : "…"}</p>
              {active && (
                <p className="text-[11px] text-slate-500">
                  {isGroup ? `${active.memberCount || (active.members || []).length} members` : `@${active.other?.username || ""}`}
                </p>
              )}
            </div>
            <div className="relative">
              <button onClick={openChatMenu} className="rounded-full p-2 text-slate-400 transition-colors hover:bg-white/10 hover:text-white" data-testid="chat-menu-button">
                <MoreVertical size={17} />
              </button>
              {chatMenuOpen && active && (
                <div className="absolute right-0 top-10 z-30 w-52 overflow-hidden rounded-xl border border-white/10 bg-surface shadow-2xl" data-testid="chat-menu">
                  {!isGroup && active.other && (
                    <Link to={`/app/creator/${active.other.id}`} onClick={() => setChatMenuOpen(false)} className="flex w-full items-center gap-2.5 px-4 py-2.5 text-left text-sm text-slate-300 hover:bg-white/5" data-testid="menu-view-profile">
                      <UserCheck size={14} /> View profile
                    </Link>
                  )}
                  {isGroup && (
                    <button onClick={() => { setChatMenuOpen(false); setSettingsOpen(true); }} className="flex w-full items-center gap-2.5 px-4 py-2.5 text-left text-sm text-slate-300 hover:bg-white/5" data-testid="menu-group-settings">
                      <Users size={14} /> Group settings
                    </button>
                  )}
                  {isGroup && active.inviteCode && (
                    <button onClick={copyGroupLink} className="flex w-full items-center gap-2.5 px-4 py-2.5 text-left text-sm text-slate-300 hover:bg-white/5" data-testid="menu-copy-group-link">
                      <Link2 size={14} /> Copy group link
                    </button>
                  )}
                  {!isGroup && active.other && (
                    <button onClick={blockUser} className="flex w-full items-center gap-2.5 px-4 py-2.5 text-left text-sm text-slate-300 hover:bg-white/5" data-testid="menu-block-user">
                      <Ban size={14} /> {otherBlocked ? "Unblock user" : "Block user"}
                    </button>
                  )}
                  <button onClick={reportConversation} className="flex w-full items-center gap-2.5 px-4 py-2.5 text-left text-sm text-slate-300 hover:bg-white/5" data-testid="menu-report-chat">
                    <Flag size={14} /> Report {isGroup ? "group" : "user"}
                  </button>
                  <button onClick={deleteChat} className="flex w-full items-center gap-2.5 px-4 py-2.5 text-left text-sm text-crimson hover:bg-crimson/10" data-testid="menu-delete-chat">
                    <Trash2 size={14} /> Delete chat
                  </button>
                </div>
              )}
            </div>
          </div>

          {active && isRequest(active) && (
            <div className="flex items-center justify-between gap-3 border-b border-amber-500/25 bg-amber-500/10 px-4 py-3" data-testid="request-banner">
              <p className="text-xs text-amber-200">Message request — accept to chat, decline to remove it.</p>
              <div className="flex shrink-0 gap-2">
                <button onClick={() => answerRequest("accept")} className="flex items-center gap-1.5 rounded-full bg-crimson px-4 py-1.5 text-xs font-bold text-white hover:bg-crimson-hover" data-testid="request-accept">
                  <Check size={12} /> Accept
                </button>
                <button onClick={() => answerRequest("decline")} className="flex items-center gap-1.5 rounded-full border border-white/15 px-4 py-1.5 text-xs font-bold text-slate-200 hover:border-crimson/50" data-testid="request-decline">
                  <X size={12} /> Decline
                </button>
              </div>
            </div>
          )}

          {pinnedMsg && (
            <div className="flex items-center gap-2 border-b border-white/10 bg-white/[0.03] px-4 py-2" data-testid="pinned-banner">
              <Pin size={12} className="shrink-0 text-crimson" />
              <p className="truncate text-xs text-slate-300">{pinnedMsg.text || pinnedMsg.type}</p>
            </div>
          )}

          {notice && <p className="border-b border-white/10 bg-crimson/10 px-4 py-2 text-center text-xs font-semibold text-crimson" data-testid="chat-notice">{notice}</p>}

          <div className="flex-1 space-y-3 overflow-y-auto p-4" onClick={() => { setMenuFor(null); setChatMenuOpen(false); }}>
            {messages.map((m, i) => {
              const mine = (m.senderId || m.sender?.id || m.userId) === user?.id;
              const deleted = isDeleted(m);
              const bare = m.type === "sticker";
              return (
                <div key={m.id || i} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                  <div className="relative">
                    {menuFor === m.id && !deleted && (
                      <MessageMenu
                        m={m}
                        mine={mine}
                        pinned={!!m.pinned}
                        onPick={(emoji) => react(m, emoji)}
                        onPin={() => pinMessage(m)}
                        onReport={() => reportMessage(m)}
                        onDeleteForMe={() => deleteForMe(m)}
                        onDeleteForEveryone={() => deleteForEveryone(m)}
                      />
                    )}
                    {deleted ? (
                      <div className="flex items-center gap-2" data-testid={`message-deleted-${m.id}`}>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            deleteForMe(m);
                          }}
                          title="Remove this notice from your chat"
                          className={`max-w-[75%] rounded-2xl border border-dashed px-4 py-2.5 text-sm italic ${mine ? "border-white/15 text-slate-500" : "border-white/10 text-slate-600"}`}
                        >
                          This message was deleted · tap to remove
                        </button>
                      </div>
                    ) : (
                      <div
                        className={`${bare ? "" : `max-w-[75%] cursor-pointer rounded-2xl px-4 py-2.5 text-sm ${mine ? "bg-crimson text-white" : "bg-white/[0.06] text-slate-200"}`}`}
                        data-testid={`message-${m.id || i}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          setMenuFor((cur) => (cur === m.id ? null : m.id));
                        }}
                      >
                        {m.pinned && <Pin size={10} className={`mb-1 ${mine ? "text-white/70" : "text-crimson"}`} />}
                        <MessageBody m={m} mine={mine} />
                        {!bare && (
                          <p className={`mt-1 text-right font-mono text-[9px] ${mine ? "text-white/60" : "text-slate-600"}`}>{timeAgo(m.createdAt)}</p>
                        )}
                      </div>
                    )}
                    {!deleted && <ReactionPills m={m} mine={mine} myId={user?.id} onToggle={(emoji) => react(m, emoji)} />}
                  </div>
                </div>
              );
            })}
            {typing && <p className="text-xs text-slate-500" data-testid="typing-indicator">typing…</p>}
            <div ref={endRef} />
          </div>
          {recError && <p className="px-4 pb-1 text-xs text-crimson" data-testid="voice-error">{recError}</p>}
          {canSend ? (
            <form onSubmit={send} className="flex items-center gap-2 border-t border-white/10 p-3">
              <input ref={fileInput} type="file" className="hidden" data-testid="chat-file-input" onChange={(e) => sendFile(e.target.files?.[0])} />
              <button
                type="button"
                onClick={() => fileInput.current?.click()}
                disabled={busy || recording}
                title="Send a photo, video, audio or document"
                className="rounded-full p-2.5 text-slate-400 transition-colors hover:bg-white/10 hover:text-white disabled:opacity-40"
                data-testid="chat-attach-button"
              >
                {busy ? <Loader2 size={17} className="animate-spin" /> : <Paperclip size={17} />}
              </button>
              {recording ? (
                <div className="flex flex-1 items-center gap-3 rounded-full border border-crimson/40 bg-crimson/10 px-4 py-2.5" data-testid="voice-recording-bar">
                  <span className="relative flex h-2 w-2">
                    <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-crimson opacity-60" />
                    <span className="relative inline-flex h-2 w-2 rounded-full bg-crimson" />
                  </span>
                  <span className="font-mono text-xs text-crimson">Recording… {fmtDur(recSeconds)}</span>
                </div>
              ) : (
                <input
                  value={text}
                  onChange={onChange}
                  placeholder="Write a message…"
                  data-testid="chat-input"
                  className="flex-1 rounded-full border border-white/10 bg-white/[0.04] px-4 py-2.5 text-sm text-white placeholder-slate-600 outline-none focus:border-crimson/60"
                />
              )}
              <button
                type="button"
                onClick={recording ? stopRecording : startRecording}
                disabled={busy}
                title={recording ? "Stop and send voice note" : "Record a voice note"}
                className={`rounded-full p-2.5 transition-colors disabled:opacity-40 ${recording ? "bg-crimson text-white hover:bg-crimson-hover" : "text-slate-400 hover:bg-white/10 hover:text-white"}`}
                data-testid="chat-voice-button"
              >
                {recording ? <Square size={16} /> : <Mic size={17} />}
              </button>
              <button className="rounded-full bg-crimson p-3 text-white hover:bg-crimson-hover" data-testid="chat-send-button">
                <Send size={16} />
              </button>
            </form>
          ) : (
            <div className="border-t border-white/10 p-4 text-center text-xs text-slate-500" data-testid="chat-locked-notice">
              This group is locked — only admins can send messages.
            </div>
          )}
        </>
      ) : (
        <div className="flex flex-1 items-center justify-center text-sm text-slate-600">Pick a conversation to start chatting.</div>
      )}
    </div>
  );

  return (
    <div className="flex h-[calc(100dvh-9.5rem)] overflow-hidden rounded-2xl border border-white/10 bg-white/[0.01] lg:h-[calc(100dvh-7rem)]" data-testid="messages-page">
      {list}
      {chat}
      {newChatOpen && (
        <NewChatModal
          onClose={() => setNewChatOpen(false)}
          onOpen={(id) => {
            setNewChatOpen(false);
            loadConvs();
            if (id) navigate(`/app/messages/${id}`);
          }}
        />
      )}
      {settingsOpen && active && (
        <GroupSettingsModal
          conv={active}
          myId={user.id}
          onClose={() => setSettingsOpen(false)}
          onChanged={loadConv}
        />
      )}
    </div>
  );
};

export default Messages;
